import React, { useEffect, useMemo, useState } from 'react';

const DEFAULT_SLIDE_INTERVAL_MS = 4000;
const TRANSITION_MS = 700;

const defaultSlides = [
  {
    image: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1800&q=80',
    title: 'Membership Recruitment',
    description: 'Engagement sessions to grow membership across public services.',
    link: '/news',
  },
  {
    image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1800&q=80',
    title: 'Workplace Consultations',
    description: 'Listening to members and addressing workplace concerns.',
    link: '/news',
  },
  {
    image: 'https://images.unsplash.com/photo-1551836022-4c4c79ecde51?w=1800&q=80',
    title: 'Capacity Building',
    description: 'Training workshops to strengthen skills and representation.',
    link: '/news',
  },
  {
    image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=1800&q=80',
    title: 'Leadership Meetings',
    description: 'Strategic planning and coordination for member support.',
    link: '/news',
  },
  {
    image: 'https://images.unsplash.com/photo-1556761175-4b46a572b786?w=1800&q=80',
    title: 'Negotiations',
    description: 'Advocating for fair terms, conditions, and protections.',
    link: '/news',
  },
  {
    image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1800&q=80',
    title: 'Stakeholder Engagement',
    description: 'Partnering with stakeholders to advance workers’ interests.',
    link: '/news',
  },
  {
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1800&q=80',
    title: 'Member Support',
    description: 'Guidance and assistance for individual member matters.',
    link: '/news',
  },
  {
    image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1800&q=80',
    title: 'Awareness Programs',
    description: 'Promoting members’ rights, safety, and entitlements.',
    link: '/news',
  },
  {
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=1800&q=80',
    title: 'Industrial Representation',
    description: 'Supporting members in industrial and employment matters.',
    link: '/news',
  },
  {
    image: 'https://images.unsplash.com/photo-1523958203904-cdcb402031fd?w=1800&q=80',
    title: 'Community Outreach',
    description: 'Activities that strengthen solidarity and public awareness.',
    link: '/news',
  },
];

const ActivitiesCarousel = ({ slides, intervalMs = DEFAULT_SLIDE_INTERVAL_MS }) => {
  const resolvedSlides = useMemo(
    () => (slides && slides.length > 0 ? slides : defaultSlides),
    [slides]
  );

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [transitionEnabled, setTransitionEnabled] = useState(true);

  // Add a clone of the first slide at the end for a smooth “wrap” animation.
  const slidesWithClone = useMemo(
    () => [...resolvedSlides, { ...resolvedSlides[0], _clone: true }],
    [resolvedSlides]
  );

  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setTransitionEnabled(true);
      setCurrentIndex((prev) => prev + 1);
    }, intervalMs);

    return () => clearInterval(interval);
  }, [isPaused, intervalMs]);

  useEffect(() => {
    // Keep index valid if slides list changes via CMS
    if (currentIndex > resolvedSlides.length) setCurrentIndex(0);
  }, [currentIndex, resolvedSlides.length]);

  useEffect(() => {
    // When we hit the cloned slide, jump back to the real first slide without transition.
    if (currentIndex !== resolvedSlides.length) return;

    const timeout = setTimeout(() => {
      setTransitionEnabled(false);
      setCurrentIndex(0);
    }, TRANSITION_MS);

    return () => clearTimeout(timeout);
  }, [currentIndex, resolvedSlides.length]);

  return (
    <section className="bg-white" aria-label="Association Activities">
      <div
        className="w-full overflow-hidden"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div
          className={`flex ${transitionEnabled ? 'transition-transform' : ''}`}
          style={{
            transform: `translateX(-${currentIndex * 100}%)`,
            transitionDuration: transitionEnabled ? `${TRANSITION_MS}ms` : '0ms',
            transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        >
          {slidesWithClone.map((slide, idx) => (
            <a
              key={`${slide.title}-${idx}`}
              href={slide.link || '/news'}
              className="group relative w-full flex-shrink-0 h-[260px] sm:h-[340px] md:h-[400px] block"
              aria-label={slide.title}
            >
              <img
                src={slide.image}
                alt={slide.title}
                className="h-full w-full object-cover"
                loading="lazy"
              />

              {/* Subtle overlay for readability */}
              <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors" />

              {/* Centered caption */}
              <div className="absolute inset-0 flex items-center justify-center p-6 text-center">
                <div className="max-w-3xl rounded-3xl bg-black/45 backdrop-blur px-6 py-5 border border-white/15 shadow-lg">
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-white leading-tight">
                    {slide.title}
                  </p>
                  <p className="mt-2 text-base sm:text-lg text-white/95 leading-relaxed">
                    {slide.description}
                  </p>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ActivitiesCarousel;
export { defaultSlides as defaultActivitiesSlides };
